import React, { useEffect, useState } from "react";
import {
  Box, Paper, Typography, Grid, Card, CardContent, Chip, Select, MenuItem, FormControl, InputLabel,
  Alert, CircularProgress
} from "@mui/material";
import { useMarketingGet } from "../Campaign/hooks/useCampaign";
import { formatBdt } from "../Campaign/utils/smsParts";
import { BreakdownTable } from "../Campaign/components/CampaignResults";
import "../Campaign/Campaign.css";

const pct = (n) => `${((Number(n) || 0) * 100).toFixed(1)}%`;
const dhaka = (d) => new Date(d).toLocaleString("en-GB", { timeZone: "Asia/Dhaka", dateStyle: "medium", timeStyle: "short" });

const Stat = ({ label, value, sub }) => (
  <Grid item xs={6} md={3}>
    <Card variant="outlined">
      <CardContent>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="h6">{value}</Typography>
        {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
      </CardContent>
    </Card>
  </Grid>
);

/** Automatic reminder pushes: who they reached and who paid afterwards (plan item 12) */
const ReminderResults = () => {
  const [days, setDays] = useState(30);
  const { data, loading, error, fetch } = useMarketingGet("/marketing/reminders-report");

  useEffect(() => { fetch({ days }); }, [days]);

  return (
    <Box className="campaign-container">
      <Box className="campaign-header">
        <Typography variant="h5">Reminder Results</Typography>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Period</InputLabel>
          <Select label="Period" value={days} onChange={(e) => setDays(e.target.value)}>
            {[7, 30, 90].map((d) => <MenuItem key={d} value={d}>Last {d} days</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && !data && <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress /></Box>}

      {data && (
        <>
          {!data.measuring && <Alert severity="warning" sx={{ mb: 2 }}>Push measurement is switched off in Marketing settings, so new runs aren't recorded.</Alert>}
          {data.holdoutPercent === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              No reminder holdout is set, so these numbers count everyone who paid after a reminder, including people who would have paid anyway.
              Set "Reminder holdout" in Marketing settings (e.g. 10%) to see what the reminders really add.
            </Alert>
          )}
          {data.jobs.length === 0 && <Paper className="form-section">No reminder runs recorded in this period yet.</Paper>}
          {data.jobs.map((job) => (
            <Paper className="form-section" key={job.job}>
              <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 1 }}>
                <Typography variant="h6">{job.label}</Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Chip size="small" label={`${job.runs} run${job.runs === 1 ? "" : "s"}`} />
                  <Chip size="small" variant="outlined" label={`Last ${dhaka(job.lastRunAt)}`} />
                  {job.method === "holdout" && (
                    <Chip size="small" color={job.extraRevenue >= 0 ? "success" : "error"} label={`Extra revenue ${formatBdt(job.extraRevenue)}`} />
                  )}
                </Box>
              </Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Stat label="Members reached" value={job.sentGroup.people.toLocaleString()} sub={`${job.messages.toLocaleString()} messages${job.smsFallbacks ? ` · ${job.smsFallbacks} SMS fallbacks` : ""}`} />
                <Stat label="Paid within the window" value={job.sentGroup.payers} sub={pct(job.sentGroup.payerRate)} />
                <Stat label="Revenue" value={formatBdt(job.sentGroup.revenue)} sub={`${formatBdt(job.sentGroup.revenuePerUser)} per member`} />
                <Stat
                  label="Holdout (skipped)"
                  value={job.holdoutGroup.people.toLocaleString()}
                  sub={job.holdoutGroup.people ? `${job.holdoutGroup.payers} paid · ${pct(job.holdoutGroup.payerRate)}` : "none"}
                />
              </Grid>
              {job.byHour.length > 0 && <BreakdownTable title="By send hour" firstColumn="Sent at" rows={job.byHour} showCost={false} />}
            </Paper>
          ))}
        </>
      )}
    </Box>
  );
};

export default ReminderResults;
